import React, { useEffect } from 'react';

import {
  Box,
  Button,
  FormField,
  Loader,
  MenuItem,
  PageHeader,
  Typography,
} from 'components';
import { useNotifications } from 'hooks';

import {
  FieldError,
  useDispatch,
  useForm,
  useSelector,
  useTranslation,
} from 'third-party';

import { clearUser, loadUser, updateUserRights } from 'modules/user';

import { userSelectors } from 'selectors';

import { MultiSelectDropdown } from 'components/MultiSelectDropdown/MultiSelectDropdown';

import { assignableUserRights, camelCaseHumanized } from 'utils/app';
import { getEnumValues } from 'utils/helper';

import { APIUser, APIUserType } from 'types/api';

enum FormFields {
  userType = 'userType',
  rights = 'rights',
}
type FormData = {
  [FormFields.userType]: APIUserType;
  [FormFields.rights]: string[];
};

type CreateEditProps = {
  organizationId: string;
  onModalClose: () => void;
  userId?: string | null;
};

export const UserRightsEditForm: React.FC<CreateEditProps> = ({
  userId,
  organizationId,
  onModalClose,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const user = useSelector(userSelectors.user);
  const loadError = useSelector(userSelectors.loadUserError);

  const canLoad = !!userId && !!organizationId;

  useEffect(() => {
    if (userId && organizationId) {
      dispatch(loadUser(organizationId, userId) as any);
    }

    return () => {
      dispatch(clearUser());
    };
  }, [userId, organizationId, dispatch]);

  if (loadError || !canLoad) {
    return (
      <Box padding="1.5rem">
        <Typography variant="body1">{t('userForm.load.error')}</Typography>
      </Box>
    );
  }

  if (!user) {
    return <Loader />;
  }

  return (
    <CreateEditForm
      user={user}
      organizationId={organizationId}
      onModalClose={onModalClose}
    />
  );
};

type Props = {
  organizationId: string;
  user: APIUser | null;
  onModalClose: () => void;
};

const selectOptions = (options: APIUserType[]) => {
  return options.map((option: APIUserType) => (
    <MenuItem key={option} value={option}>
      <Typography variant="body2">{option}</Typography>
    </MenuItem>
  ));
};

const editableUserTypes = getEnumValues(APIUserType).filter(
  it => it !== APIUserType.SuperAdmin,
);

export const CreateEditForm: React.FC<Props> = ({
  user,
  organizationId,
  onModalClose,
}) => {
  const { error, success } = useNotifications();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const userOrganization = user?.userOrganizations.find(
    it => it.organization.id === organizationId,
  );

  const {
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      [FormFields.userType]: userOrganization?.userType || APIUserType.User,
      [FormFields.rights]:
        userOrganization?.userRights.map(it => it.right.toString()) || [],
    },
  });

  const isFormPending = isSubmitting;
  const userType = watch(FormFields.userType);

  // A SuperAdmin role is shown for reference only - it cannot be assigned or changed here.
  const isSuperAdmin = userOrganization?.userType === APIUserType.SuperAdmin;
  const userTypeOptions = isSuperAdmin
    ? [APIUserType.SuperAdmin]
    : editableUserTypes;

  useEffect(() => {
    if (user && userType == APIUserType.User) {
      setValue(FormFields.rights, []);
    }
    if (user && userType == APIUserType.Admin) {
      setValue(FormFields.rights, assignableUserRights);
    }
  }, [user, userType, setValue]);

  const handleCancel = () => {
    onModalClose();
  };

  const onSubmit = async (formData: FormData) => {
    if (user) {
      const result: any = await dispatch(
        updateUserRights(organizationId, user.id, formData as any) as any,
      );
      if (result.error) {
        error(result.payload.body?.title || t('userForm.edit.error'));
      } else {
        success(t('userForm.edit.sucscefull'));
        onModalClose();
      }
      return;
    }
  };

  return (
    <Box padding="1.5rem">
      <form>
        <PageHeader
          header={t('userForm.editRight.header')}
          subHeader={t('userForm.editRight.subHeader')}
        >
          <Box display="flex" columnGap="1rem">
            <Button
              type="submit"
              variant="outlined"
              onClick={handleSubmit(onSubmit)}
              size="medium"
              color="primary"
              disabled={isFormPending || isSuperAdmin}
            >
              {t('common.form.save')}
            </Button>
            <Button
              type="button"
              variant="outlined"
              onClick={handleCancel}
              size="medium"
              color="error"
            >
              {t('common.form.cancel')}
            </Button>
          </Box>
        </PageHeader>
        <FormField
          fieldName={FormFields.userType}
          fieldError={errors[FormFields.userType] as FieldError}
          label={t('membersList.column.userType')}
          placeholder={
            t('common.form.enterValueFor') + t('membersList.column.userType')
          }
          requiredErrorMessage={t('common.form.fieldRequired')}
          control={control}
          maxLength={200}
          selectOptions={selectOptions(userTypeOptions)}
          isRequired
          isDisabled={isFormPending || isSuperAdmin}
        />
        <MultiSelectDropdown
          fieldName={FormFields.rights}
          control={control}
          isDisabled={isFormPending || userType !== APIUserType.Editor}
          caption={'User access'}
          defaultSelected={userOrganization?.userRights.map(it =>
            it.right.toString(),
          )}
          options={assignableUserRights.map(it => ({
            key: it.toString(),
            value: camelCaseHumanized(it),
          }))}
        />
      </form>
      <Typography variant="body2" color="textSecondary" marginTop="1rem">
        {t('userForm.helpText')}
      </Typography>
    </Box>
  );
};
